import type { AttackProfile, Skill } from '../content/types.ts';
import type { Character } from '../domain/character.ts';
import type { SessionState } from '../domain/session.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { Roller } from '../rules/dice.ts';
import { addRolls } from '../rules/rollHistory.ts';
import type { RollHistoryEntry } from '../rules/rollHistory.ts';
import type { AdvancementCheckpoint, AdvancementRoll } from '../rules/skillAdvancement.ts';
import {
  LEVELS_PER_TWO_HOUR_CHECKPOINT,
  canBeMarkedByExplicitTrigger,
  canBeMarkedByUse,
  processCheckpoint,
} from '../rules/skillAdvancement.ts';
import { advancementHistoryEntry } from './historyEntries.ts';
import { applyChanges } from '../rules/characterChanges.ts';
import { batch } from './characterModificationService.ts';
import type { CharacterChange } from '../domain/characterChange.ts';
import { awardUntrainedRank, isTutorialFloor } from '../rules/sessionAdvancement.ts';
import { newId } from './ids.ts';

/**
 * Marking Skills through use, and processing the two advancement checkpoints.
 *
 * Kept apart from the Tutorial-Floor untrained award in sessionAwardService.ts:
 * different rule, different trigger, different outcome. The two-hour checkpoint
 * calls into that award only because the source puts both at the same moment.
 */

/**
 * RULE DCC-ADV-SKILL-001 - mark the Skill on any attempt, Succeed or Fail.
 *
 * Returns the SAME object when nothing changed, so callers can apply this
 * unconditionally without triggering a needless save.
 *
 * A Skill the crawler does not actually have is never marked. An untrained
 * attempt belongs to the Tutorial-Floor untrained system (Rules p.12), not to
 * ordinary advancement, and marking it would grant a Skill the crawler has no
 * Ranks in a route to Rank 2.
 */
export function markSkillUsed(
  character: Character,
  skillId: string,
  registry: ContentRegistry,
): Character {
  const skill = registry.getSkill(skillId);
  if (!skill || !canBeMarkedByUse(skill)) return character;
  if (!character.skills.some((s) => s.skillId === skillId && s.rank >= 1)) return character;

  if (character.advancementMarks.includes(skillId)) return character;

  // Through the shared modification layer, so the mark is validated and audited
  // like every other change to the sheet.
  const result = applyChanges(
    character,
    batch('play', 'Skill used', [{ kind: 'add-advancement-mark', skillId }]),
    { now: character.updatedAt },
  );
  return result.ok ? result.character : character;
}

/**
 * RULE DCC-ADV-SKILL-005 - an explicitly triggered Passive Skill, such as
 * Aiming being applied to a Ranged attack (C&C p.27). Marked only when the
 * caller reports that the trigger actually fired.
 */
export function markSkillExplicitTrigger(
  character: Character,
  skillId: string,
  registry: ContentRegistry,
): Character {
  const skill = registry.getSkill(skillId);
  if (!skill || !canBeMarkedByExplicitTrigger(skill)) return character;
  if (!character.skills.some((s) => s.skillId === skillId && s.rank >= 1)) return character;

  if (character.advancementMarks.includes(skillId)) return character;

  const result = applyChanges(
    character,
    batch('play', 'Explicit advancement trigger', [{ kind: 'add-advancement-mark', skillId }]),
    { now: character.updatedAt },
  );
  return result.ok ? result.character : character;
}

/** Equipment occupancy does not gate attacks; this only qualifies the advancement choice. */
export function handToHandAdvancementProblem(character: Character, attack: Skill, profile: AttackProfile, damageEffectId: string, registry: ContentRegistry): string | null {
  const effect = registry.getSkill(damageEffectId);
  return attack.kind === 'melee-attack' && profile.weaponUse === null && effect?.advancement === 'hand-to-hand-choice' && character.skills.some((s) => s.skillId === damageEffectId && s.rank > 0) ? null : 'Choose a trained Damage Effect actually used with this hand-to-hand attack.';
}

/** The caller confirms the Damage Effect was actually used on this attack. */
export function markHandToHandDamageEffect(character: Character, skillId: string, registry: ContentRegistry): Character {
  if (registry.getSkill(skillId)?.advancement !== 'hand-to-hand-choice' || !character.skills.some((s) => s.skillId === skillId && s.rank > 0)) return character;
  const result = applyChanges(character, batch('play', 'Hand-to-hand Damage Effect used instead of marking its Attack', [{ kind: 'add-advancement-mark', skillId }]), { now: character.updatedAt });
  return result.ok ? result.character : character;
}

export interface CheckpointOutcome {
  character: Character;
  session: SessionState;
  rolls: AdvancementRoll[];
  /** Human-readable lines for the confirmation panel. */
  lines: string[];
  levelsGained: number;
  /** Candidates the player must now resolve, if any. */
  pendingAwardCandidates: string[];
}

export type CheckpointAttempt =
  | ({ ok: true } & CheckpointOutcome)
  | { ok: false; reason: string };

function describe(roll: AdvancementRoll, name: string): string {
  if (roll.atCap) {
    return `${name} Rank ${roll.rankBefore} - already at the Rank cap, no roll made`;
  }
  return (
    `${name} Rank ${roll.rankBefore} | Advancement roll: ${roll.die} vs ${roll.rankBefore} | ` +
    (roll.success ? `SUCCESS -> Rank ${roll.rankAfter}` : 'FAIL - Rank unchanged')
  );
}

function runCheckpoint(
  roller: Roller,
  character: Character,
  session: SessionState,
  registry: ContentRegistry,
  checkpoint: AdvancementCheckpoint,
  now: string,
): CheckpointOutcome {
  const result = processCheckpoint(roller, {
    skills: character.skills,
    marks: character.advancementMarks,
    checkpoint,
    canResolveSkill: (id) => registry.getSkill(id) !== undefined,
    maxRankFor: (id) => Math.min(character.skillRankLimits?.[id] ?? 15, registry.getSkill(id)?.maxRank ?? 20),
  });

  const entries: RollHistoryEntry[] = [];
  const lines: string[] = [];
  for (const roll of result.rolls) {
    const name = registry.getSkill(roll.skillId)?.name ?? roll.skillId;
    lines.push(describe(roll, name));
    if (!roll.atCap) {
      entries.push(advancementHistoryEntry({ id: newId(), timestamp: now, roll, skillName: name }));
    }
  }

  for (const id of character.advancementMarks) if (!registry.getSkill(id)) lines.push(`${id}: content unavailable; advancement mark retained.`);

  // Every Rank gained and every mark cleared goes through the shared layer.
  const resolvedMarks = character.advancementMarks.filter(
    (id) => !result.remainingMarks.includes(id),
  );
  const changes: CharacterChange[] = [
    ...result.rolls
      .filter((roll) => roll.success)
      .map((roll): CharacterChange => ({
        kind: 'set-skill-rank',
        skillId: roll.skillId,
        rank: roll.rankAfter,
      })),
    { kind: 'clear-advancement-marks', skillIds: resolvedMarks },
  ];
  const applied = applyChanges(
    character,
    batch('advancement', `${checkpoint} checkpoint`, changes),
    { now, maxSkillRank: 20, lookupSkill: (id) => registry.getSkill(id) },
  );

  if (!applied.ok) throw new Error(`Skill checkpoint could not be applied: ${applied.problems.map((p) => p.message).join('; ')}`);
  return {
    character: applied.character,
    session: { ...session, rollHistory: addRolls(session.rollHistory, entries) },
    rolls: result.rolls,
    lines,
    levelsGained: 0,
    pendingAwardCandidates: [],
  };
}

/**
 * RULE DCC-ADV-CHECKPOINT-001 - the 2-hour checkpoint.
 *
 * Represents two hours of play as judged by the table, NOT wall-clock time. It
 * grants 1 Level (C&C p.43) and resolves marked Rank 1-4 Skills (C&C p.42).
 * Marks on Rank 5+ Skills are deliberately left for the floor checkpoint.
 *
 * This does not end the application session: a four-hour sitting can process
 * two checkpoints.
 */
export function processTwoHourCheckpoint(
  roller: Roller,
  character: Character,
  session: SessionState,
  registry: ContentRegistry,
  now: string,
): CheckpointAttempt {
  // An unresolved award from the previous interval must be settled first, or
  // candidates from two different intervals would be indistinguishable.
  if (session.pendingUntrainedAward !== null) {
    return {
      ok: false,
      reason:
        'The previous 2-hour checkpoint left an untrained Skill award unresolved. Claim or discard it before processing another checkpoint.',
    };
  }

  const outcome = runCheckpoint(roller, character, session, registry, 'two-hour', now);

  // RULE DCC-SESS-002 - snapshot this interval's untrained attempts and start a
  // fresh list immediately. The session itself continues: same startedAt, same
  // roll history.
  const candidates = isTutorialFloor(character.floor)
    ? [...outcome.session.untrainedSkillsAttempted]
    : [];

  return {
    ok: true,
    ...outcome,
    character: levelUp(outcome.character, now),
    session: {
      ...outcome.session,
      untrainedSkillsAttempted: [],
      pendingUntrainedAward:
        candidates.length > 0 ? { candidates, openedAt: now } : null,
    },
    levelsGained: character.level < 250 ? LEVELS_PER_TWO_HOUR_CHECKPOINT : 0,
    pendingAwardCandidates: candidates,
  };
}

export type PendingAwardOutcome =
  | { ok: true; character: Character; session: SessionState }
  | { ok: false; reason: string };

/**
 * Claims the award snapshotted by the last 2-hour checkpoint.
 * The session continues unchanged apart from the award being settled.
 */
export function claimPendingUntrainedAward(
  character: Character,
  session: SessionState,
  chosenSkillId: string,
): PendingAwardOutcome {
  const pending = session.pendingUntrainedAward;
  if (!pending) return { ok: false, reason: 'There is no untrained Skill award waiting.' };

  const awarded = awardUntrainedRank({
    skills: character.skills,
    floor: character.floor,
    candidates: pending.candidates,
    chosenSkillId,
  });
  if (!awarded.ok) return awarded;

  const applied = applyChanges(
    character,
    batch('session-award', 'End-of-interval untrained Skill award', [
      { kind: 'add-skill', skillId: chosenSkillId, rank: 1 },
    ]),
    { now: character.updatedAt },
  );
  if (!applied.ok) {
    return { ok: false, reason: applied.problems.map((p) => p.message).join('; ') };
  }

  return {
    ok: true,
    character: applied.character,
    session: { ...session, pendingUntrainedAward: null },
  };
}

/** Throws the snapshotted candidates away. Only ever after an explicit confirmation. */
export function discardPendingUntrainedAward(session: SessionState): SessionState {
  return { ...session, pendingUntrainedAward: null };
}

/**
 * RULE DCC-ADV-CHECKPOINT-002 - the end-of-floor checkpoint.
 *
 * Resolves marked Rank 5+ Skills. It does NOT change the Floor Number: that is
 * a separate, explicitly confirmed action, because descending has consequences
 * this application does not model.
 */
export function processEndOfFloorCheckpoint(
  roller: Roller,
  character: Character,
  session: SessionState,
  registry: ContentRegistry,
  now: string,
): CheckpointOutcome {
  // The floor checkpoint never touches the untrained award: that belongs to the
  // two-hour interval (Rules for Survival p.12).
  return runCheckpoint(roller, character, session, registry, 'end-of-floor', now);
}

/** Separate, deliberate action. Never bundled into a checkpoint. */
export function advanceFloor(character: Character): Character {
  const result = applyChanges(
    character,
    batch('play', 'Descended a floor', [{ kind: 'set-floor', floor: character.floor + 1 }]),
    { now: character.updatedAt },
  );
  return result.ok ? result.character : character;
}

/** RULE DCC-ADV-LEVEL-001 - one Level per 2-hour checkpoint (C&C p.43). */
function levelUp(character: Character, now: string): Character {
  if (character.level >= 250) return character;
  const result = applyChanges(
    character,
    batch('advancement', '2-hour checkpoint', [
      { kind: 'adjust-level', delta: LEVELS_PER_TWO_HOUR_CHECKPOINT },
    ]),
    { now },
  );
  return result.ok ? result.character : character;
}
